// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Clickable, { ClickablePresentationProps } from "@pace/MobiusOverrides/Clickable/Clickable";
import { DynamicDropdownPresentationProps } from "@pace/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import FindLocationModal from "@pace/Overrides/Components/FindLocationModal";
import IconEdit from "@pace/XDL/Icons/IconEdit";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const warehousesDataView = getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter);
    const sortedWarehouses = (warehousesDataView.value || []).slice().sort((a, b) => {
        const numA = parseInt(a.name, 10);
        const numB = parseInt(b.name, 10);
        
        if (numA < numB) {
            return -1;
        }
        if (numA > numB) {
            return 1;
        }
        return 0;
    });
    const dropDownOptions: OptionObject[] = sortedWarehouses.map(warehouse => ({
        optionText: `${warehouse.name} - ${warehouse.description} - ${warehouse.address1} ${warehouse.address2} ${warehouse.city} ${warehouse.state} ${warehouse.postalCode} ${warehouse.phone}`,
        optionValue: warehouse.id,
    }));
    const warehousesHashMap = warehousesDataView.value?.reduce<Dictionary<WarehouseModel>>((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
    }, {});
    return {
        session: state.context.session,
        enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        dropDownOptions: dropDownOptions || {},
        warehousesHashMap: warehousesHashMap || {},
        address: getSession(state).pickUpWarehouse,
    };
};

const mapDispatchToProps = {
    updatePickUpWarehouse,
    loadWarehouses,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderWarehouseAddress {
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    titleLabelTypography?: TypographyPresentationProps;
    warehousesDynamicDropdown?: DynamicDropdownPresentationProps;
    branchWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
}

export const headerWarehouseAddress: HeaderWarehouseAddress = {};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderWarehouseAddress: FC<Props> = ({
    id,
    session,
    enableWarehousePickup,
    updatePickUpWarehouse,
    dropDownOptions,
    warehousesHashMap,
    address,
    loadWarehouses,
}) => {
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.currentTarget.value);
    };
    React.useEffect(() => {
        loadWarehouses({
            radius: 999999,
            onlyPickupWarehouses: true,
            pageSize: 40,
            search,
            page: 1,
            latitude: 0,
            longitude: 0,
        });
    }, [search]);

    const handleWarehouseSelected = async (warehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse: warehouse });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
        setIsFindLocationOpen(false);
    };

    const toasterContext = React.useContext(ToasterContext);

    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };

    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const styles = useMergeStyles("headerShipToAddress", headerWarehouseAddress);

    if (!enableWarehousePickup || !session.pickUpWarehouse) {
        return null;
    }

    return (
        <>
            <StyledWrapper
                {...styles.branchWrapper}
                data-test-selector="header_branchButton"
            >
                {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                <Typography {...styles.titleLabelTypography}>{translate("Branch")}:</Typography>
                <DynamicDropdown
                    label={translate("Branch")}
                    options={dropDownOptions}
                    selected={address?.id}
                    onInputChange={onInputChanged}
                    onSelectionChange={warehouseId => {
                        if (!warehouseId || !warehousesHashMap[warehouseId]) {
                            return;
                        }
                        handleWarehouseSelected(warehousesHashMap[warehouseId]);
                    }}
                    {...styles.warehousesDynamicDropdown}
                />
                <Clickable onClick={handleOpenFindLocation} {...styles.titleClickable}>
                    <Icon {...styles.titleIcon} src={IconEdit} />
                </Clickable>
                <FindLocationModal
                    modalIsOpen={isFindLocationOpen}
                    onWarehouseSelected={handleWarehouseSelected}
                    onModalClose={handleFindLocationModalClose}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderWarehouseAddress)),
    definition: {
        displayName: "Header Warehouse Address",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
